import {
  queryOptions as queryOptionsBuilder,
  useMutation,
  useQuery,
  useSuspenseQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult
} from '@tanstack/react-query';

import type {
  AuthLoginResponse,
  CreateGroupRequest,
  GroupInviteResponse,
  RespondGroupInviteRequest,
  SendInviteGroupRequest,
  UpdateGroupRequest,
  UserGroupResponse
} from '../model';

import { customInstance } from '../../mutator/custom-instance-new.ts';
import type { ErrorType , BodyType } from '../../mutator/custom-instance-new.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Set a group as the current user default
 */
export const groupSetDefault = (
    id: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/group/${id}/set-default`, method: 'PUT', signal
    },
      options);
    }




export const getGroupSetDefaultMutationKey = () => ['groupSetDefault'] as const;

export const getGroupSetDefaultMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupSetDefault>>, TError,GroupSetDefaultMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupSetDefault>>, TError,GroupSetDefaultMutationVariables, TContext> => {

const mutationKey = getGroupSetDefaultMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupSetDefault>>, GroupSetDefaultMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  groupSetDefault(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupSetDefaultMutationResult = NonNullable<Awaited<ReturnType<typeof groupSetDefault>>>

    export type GroupSetDefaultMutationError = ErrorType<void>
    export type GroupSetDefaultMutationVariables = {id: number}

    /**
 * @summary Set a group as the current user default
 */
export const useGroupSetDefault = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupSetDefault>>, TError,GroupSetDefaultMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupSetDefault>>,
        TError,
        GroupSetDefaultMutationVariables,
        TContext
      > => {
      return useMutation(getGroupSetDefaultMutationOptions(options), queryClient);
    }
    /**
 * @summary Remove a user from a group
 */
export const groupRemoveUser = (
    id: number,
    userId: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/group/${id}/user/${userId}`, method: 'DELETE', signal
    },
      options);
    }




export const getGroupRemoveUserMutationKey = () => ['groupRemoveUser'] as const;

export const getGroupRemoveUserMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupRemoveUser>>, TError,GroupRemoveUserMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupRemoveUser>>, TError,GroupRemoveUserMutationVariables, TContext> => {

const mutationKey = getGroupRemoveUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupRemoveUser>>, GroupRemoveUserMutationVariables> = (props) => {
          const {id,userId} = props ?? {};

          return  groupRemoveUser(id,userId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupRemoveUserMutationResult = NonNullable<Awaited<ReturnType<typeof groupRemoveUser>>>

    export type GroupRemoveUserMutationError = ErrorType<void>
    export type GroupRemoveUserMutationVariables = {id: number;userId: number}

    /**
 * @summary Remove a user from a group
 */
export const useGroupRemoveUser = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupRemoveUser>>, TError,GroupRemoveUserMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupRemoveUser>>,
        TError,
        GroupRemoveUserMutationVariables,
        TContext
      > => {
      return useMutation(getGroupRemoveUserMutationOptions(options), queryClient);
    }
    /**
 * @summary Request deletion of a group owned by the current user
 */
export const groupDeleteGroup = (
    id: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/group/${id}`, method: 'DELETE', signal
    },
      options);
    }




export const getGroupDeleteGroupMutationKey = () => ['groupDeleteGroup'] as const;

export const getGroupDeleteGroupMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupDeleteGroup>>, TError,GroupDeleteGroupMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupDeleteGroup>>, TError,GroupDeleteGroupMutationVariables, TContext> => {

const mutationKey = getGroupDeleteGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupDeleteGroup>>, GroupDeleteGroupMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  groupDeleteGroup(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupDeleteGroupMutationResult = NonNullable<Awaited<ReturnType<typeof groupDeleteGroup>>>

    export type GroupDeleteGroupMutationError = ErrorType<void>
    export type GroupDeleteGroupMutationVariables = {id: number}

    /**
 * @summary Request deletion of a group owned by the current user
 */
export const useGroupDeleteGroup = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupDeleteGroup>>, TError,GroupDeleteGroupMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupDeleteGroup>>,
        TError,
        GroupDeleteGroupMutationVariables,
        TContext
      > => {
      return useMutation(getGroupDeleteGroupMutationOptions(options), queryClient);
    }
    /**
 * @summary Update a group the current user is part of
 */
export const groupUpdate = (
    id: number,
    updateGroupRequest: BodyType<UpdateGroupRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/group/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateGroupRequest, signal
    },
      options);
    }




export const getGroupUpdateMutationKey = () => ['groupUpdate'] as const;

export const getGroupUpdateMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupUpdate>>, TError,GroupUpdateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupUpdate>>, TError,GroupUpdateMutationVariables, TContext> => {

const mutationKey = getGroupUpdateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupUpdate>>, GroupUpdateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  groupUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof groupUpdate>>>
    export type GroupUpdateMutationBody = BodyType<UpdateGroupRequest>
    export type GroupUpdateMutationError = ErrorType<void>
    export type GroupUpdateMutationVariables = {id: number;data: BodyType<UpdateGroupRequest>}

    /**
 * @summary Update a group the current user is part of
 */
export const useGroupUpdate = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupUpdate>>, TError,GroupUpdateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupUpdate>>,
        TError,
        GroupUpdateMutationVariables,
        TContext
      > => {
      return useMutation(getGroupUpdateMutationOptions(options), queryClient);
    }
    /**
 * @summary Cancel a pending deletion of a group owned by the current user
 */
export const groupCancelDeletion = (
    id: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/group/${id}/cancel-deletion`, method: 'POST', signal
    },
      options);
    }




export const getGroupCancelDeletionMutationKey = () => ['groupCancelDeletion'] as const;

export const getGroupCancelDeletionMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupCancelDeletion>>, TError,GroupCancelDeletionMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupCancelDeletion>>, TError,GroupCancelDeletionMutationVariables, TContext> => {

const mutationKey = getGroupCancelDeletionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupCancelDeletion>>, GroupCancelDeletionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  groupCancelDeletion(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupCancelDeletionMutationResult = NonNullable<Awaited<ReturnType<typeof groupCancelDeletion>>>

    export type GroupCancelDeletionMutationError = ErrorType<void>
    export type GroupCancelDeletionMutationVariables = {id: number}

    /**
 * @summary Cancel a pending deletion of a group owned by the current user
 */
export const useGroupCancelDeletion = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupCancelDeletion>>, TError,GroupCancelDeletionMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupCancelDeletion>>,
        TError,
        GroupCancelDeletionMutationVariables,
        TContext
      > => {
      return useMutation(getGroupCancelDeletionMutationOptions(options), queryClient);
    }
    /**
 * @summary List all groups the current user is part of
 */
export const groupListForUser = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<UserGroupResponse[]>(
      {url: `/groups`, method: 'GET', signal
    },
      options);
    }




export const getGroupListForUserQueryKey = () => {
    return [
    `/groups`
    ] as const;
    }


export const getGroupListForUserQueryOptions = <TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupListForUserQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupListForUser>>> = ({ signal }) => groupListForUser(requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GroupListForUserQueryResult = NonNullable<Awaited<ReturnType<typeof groupListForUser>>>
export type GroupListForUserQueryError = ErrorType<unknown>


export function useGroupListForUser<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupListForUser>>,
          TError,
          Awaited<ReturnType<typeof groupListForUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupListForUser<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupListForUser>>,
          TError,
          Awaited<ReturnType<typeof groupListForUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupListForUser<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List all groups the current user is part of
 */

export function useGroupListForUser<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupListForUserQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGroupListForUserSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>( options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupListForUserQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupListForUser>>> = ({ signal }) => groupListForUser(requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type GroupListForUserSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof groupListForUser>>>
export type GroupListForUserSuspenseQueryError = ErrorType<unknown>


export function useGroupListForUserSuspense<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupListForUserSuspense<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupListForUserSuspense<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List all groups the current user is part of
 */

export function useGroupListForUserSuspense<TData = Awaited<ReturnType<typeof groupListForUser>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupListForUser>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupListForUserSuspenseQueryOptions(options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary Create a new group for the current user
 */
export const groupCreate = (
    createGroupRequest: BodyType<CreateGroupRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<AuthLoginResponse>(
      {url: `/group`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createGroupRequest, signal
    },
      options);
    }




export const getGroupCreateMutationKey = () => ['groupCreate'] as const;

export const getGroupCreateMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupCreate>>, TError,GroupCreateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupCreate>>, TError,GroupCreateMutationVariables, TContext> => {

const mutationKey = getGroupCreateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupCreate>>, GroupCreateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  groupCreate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupCreateMutationResult = NonNullable<Awaited<ReturnType<typeof groupCreate>>>
    export type GroupCreateMutationBody = BodyType<CreateGroupRequest>
    export type GroupCreateMutationError = ErrorType<void>
    export type GroupCreateMutationVariables = {data: BodyType<CreateGroupRequest>}

    /**
 * @summary Create a new group for the current user
 */
export const useGroupCreate = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupCreate>>, TError,GroupCreateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupCreate>>,
        TError,
        GroupCreateMutationVariables,
        TContext
      > => {
      return useMutation(getGroupCreateMutationOptions(options), queryClient);
    }
    /**
 * Requires a confirmed address, because this is the only action that mails a third party under our domain. Rejects with 403 `auth.email.not.verified`.
 * @summary Send an invite to a user to join a group owned by the current user
 */
export const groupInviteSend = (
    id: number,
    sendInviteGroupRequest: BodyType<SendInviteGroupRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<unknown>(
      {url: `/group/${id}/send-invite`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: sendInviteGroupRequest, signal
    },
      options);
    }




export const getGroupInviteSendMutationKey = () => ['groupInviteSend'] as const;

export const getGroupInviteSendMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupInviteSend>>, TError,GroupInviteSendMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupInviteSend>>, TError,GroupInviteSendMutationVariables, TContext> => {

const mutationKey = getGroupInviteSendMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupInviteSend>>, GroupInviteSendMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  groupInviteSend(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupInviteSendMutationResult = NonNullable<Awaited<ReturnType<typeof groupInviteSend>>>
    export type GroupInviteSendMutationBody = BodyType<SendInviteGroupRequest>
    export type GroupInviteSendMutationError = ErrorType<void>
    export type GroupInviteSendMutationVariables = {id: number;data: BodyType<SendInviteGroupRequest>}

    /**
 * @summary Send an invite to a user to join a group owned by the current user
 */
export const useGroupInviteSend = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupInviteSend>>, TError,GroupInviteSendMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupInviteSend>>,
        TError,
        GroupInviteSendMutationVariables,
        TContext
      > => {
      return useMutation(getGroupInviteSendMutationOptions(options), queryClient);
    }
    /**
 * @summary Accept or reject an invite
 */
export const groupInviteRespond = (
    code: string,
    respondGroupInviteRequest: BodyType<RespondGroupInviteRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/groups/invite/${code}`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: respondGroupInviteRequest, signal
    },
      options);
    }




export const getGroupInviteRespondMutationKey = () => ['groupInviteRespond'] as const;

export const getGroupInviteRespondMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupInviteRespond>>, TError,GroupInviteRespondMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupInviteRespond>>, TError,GroupInviteRespondMutationVariables, TContext> => {

const mutationKey = getGroupInviteRespondMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupInviteRespond>>, GroupInviteRespondMutationVariables> = (props) => {
          const {code,data} = props ?? {};

          return  groupInviteRespond(code,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupInviteRespondMutationResult = NonNullable<Awaited<ReturnType<typeof groupInviteRespond>>>
    export type GroupInviteRespondMutationBody = BodyType<RespondGroupInviteRequest>
    export type GroupInviteRespondMutationError = ErrorType<void>
    export type GroupInviteRespondMutationVariables = {code: string;data: BodyType<RespondGroupInviteRequest>}

    /**
 * @summary Accept or reject an invite
 */
export const useGroupInviteRespond = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupInviteRespond>>, TError,GroupInviteRespondMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupInviteRespond>>,
        TError,
        GroupInviteRespondMutationVariables,
        TContext
      > => {
      return useMutation(getGroupInviteRespondMutationOptions(options), queryClient);
    }
    /**
 * @summary List the invites for a group owned by the current user
 */
export const groupInviteListGroupInvites = (
    id: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<GroupInviteResponse[]>(
      {url: `/group/${id}/invites`, method: 'GET', signal
    },
      options);
    }




export const getGroupInviteListGroupInvitesQueryKey = (id: number,) => {
    return [
    `/group/${id}/invites`
    ] as const;
    }


export const getGroupInviteListGroupInvitesQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(id: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteListGroupInvitesQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteListGroupInvites>>> = ({ signal }) => groupInviteListGroupInvites(id, requestOptions, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GroupInviteListGroupInvitesQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteListGroupInvites>>>
export type GroupInviteListGroupInvitesQueryError = ErrorType<void>


export function useGroupInviteListGroupInvites<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteListGroupInvites>>,
          TError,
          Awaited<ReturnType<typeof groupInviteListGroupInvites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListGroupInvites<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteListGroupInvites>>,
          TError,
          Awaited<ReturnType<typeof groupInviteListGroupInvites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListGroupInvites<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the invites for a group owned by the current user
 */

export function useGroupInviteListGroupInvites<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteListGroupInvitesQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGroupInviteListGroupInvitesSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(id: number, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteListGroupInvitesQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteListGroupInvites>>> = ({ signal }) => groupInviteListGroupInvites(id, requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type GroupInviteListGroupInvitesSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteListGroupInvites>>>
export type GroupInviteListGroupInvitesSuspenseQueryError = ErrorType<void>


export function useGroupInviteListGroupInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListGroupInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListGroupInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the invites for a group owned by the current user
 */

export function useGroupInviteListGroupInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError = ErrorType<void>>(
 id: number, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListGroupInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteListGroupInvitesSuspenseQueryOptions(id,options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary List the invites for the current user
 */
export const groupInviteListUserInvites = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<GroupInviteResponse[]>(
      {url: `/group/user-invites`, method: 'GET', signal
    },
      options);
    }




export const getGroupInviteListUserInvitesQueryKey = () => {
    return [
    `/group/user-invites`
    ] as const;
    }


export const getGroupInviteListUserInvitesQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteListUserInvitesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteListUserInvites>>> = ({ signal }) => groupInviteListUserInvites(requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GroupInviteListUserInvitesQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteListUserInvites>>>
export type GroupInviteListUserInvitesQueryError = ErrorType<void>


export function useGroupInviteListUserInvites<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteListUserInvites>>,
          TError,
          Awaited<ReturnType<typeof groupInviteListUserInvites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListUserInvites<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteListUserInvites>>,
          TError,
          Awaited<ReturnType<typeof groupInviteListUserInvites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListUserInvites<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the invites for the current user
 */

export function useGroupInviteListUserInvites<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteListUserInvitesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGroupInviteListUserInvitesSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>( options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteListUserInvitesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteListUserInvites>>> = ({ signal }) => groupInviteListUserInvites(requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type GroupInviteListUserInvitesSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteListUserInvites>>>
export type GroupInviteListUserInvitesSuspenseQueryError = ErrorType<void>


export function useGroupInviteListUserInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListUserInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteListUserInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the invites for the current user
 */

export function useGroupInviteListUserInvitesSuspense<TData = Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError = ErrorType<void>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteListUserInvites>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteListUserInvitesSuspenseQueryOptions(options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary Validate an invitation code (public endpoint)
 */
export const groupInviteValidateInvite = (
    code: string,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/invitation/validate/${code}`, method: 'GET', signal
    },
      options);
    }




export const getGroupInviteValidateInviteQueryKey = (code: string,) => {
    return [
    `/invitation/validate/${code}`
    ] as const;
    }


export const getGroupInviteValidateInviteQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteValidateInviteQueryKey(code);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteValidateInvite>>> = ({ signal }) => groupInviteValidateInvite(code, requestOptions, signal);





   return  { queryKey, queryFn, enabled: code !== null && code !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GroupInviteValidateInviteQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteValidateInvite>>>
export type GroupInviteValidateInviteQueryError = ErrorType<void>


export function useGroupInviteValidateInvite<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteValidateInvite>>,
          TError,
          Awaited<ReturnType<typeof groupInviteValidateInvite>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteValidateInvite<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteValidateInvite>>,
          TError,
          Awaited<ReturnType<typeof groupInviteValidateInvite>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteValidateInvite<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Validate an invitation code (public endpoint)
 */

export function useGroupInviteValidateInvite<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteValidateInviteQueryOptions(code,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGroupInviteValidateInviteSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteValidateInviteQueryKey(code);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteValidateInvite>>> = ({ signal }) => groupInviteValidateInvite(code, requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type GroupInviteValidateInviteSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteValidateInvite>>>
export type GroupInviteValidateInviteSuspenseQueryError = ErrorType<void>


export function useGroupInviteValidateInviteSuspense<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteValidateInviteSuspense<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteValidateInviteSuspense<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Validate an invitation code (public endpoint)
 */

export function useGroupInviteValidateInviteSuspense<TData = Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteValidateInvite>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteValidateInviteSuspenseQueryOptions(code,options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary Get invitation details by code (public endpoint)
 */
export const groupInviteGetInviteDetails = (
    code: string,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<GroupInviteResponse>(
      {url: `/invitation/details/${code}`, method: 'GET', signal
    },
      options);
    }




export const getGroupInviteGetInviteDetailsQueryKey = (code: string,) => {
    return [
    `/invitation/details/${code}`
    ] as const;
    }


export const getGroupInviteGetInviteDetailsQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteGetInviteDetailsQueryKey(code);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>> = ({ signal }) => groupInviteGetInviteDetails(code, requestOptions, signal);





   return  { queryKey, queryFn, enabled: code !== null && code !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GroupInviteGetInviteDetailsQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>>
export type GroupInviteGetInviteDetailsQueryError = ErrorType<void>


export function useGroupInviteGetInviteDetails<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteGetInviteDetails>>,
          TError,
          Awaited<ReturnType<typeof groupInviteGetInviteDetails>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteGetInviteDetails<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof groupInviteGetInviteDetails>>,
          TError,
          Awaited<ReturnType<typeof groupInviteGetInviteDetails>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteGetInviteDetails<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get invitation details by code (public endpoint)
 */

export function useGroupInviteGetInviteDetails<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteGetInviteDetailsQueryOptions(code,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGroupInviteGetInviteDetailsSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGroupInviteGetInviteDetailsQueryKey(code);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>> = ({ signal }) => groupInviteGetInviteDetails(code, requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type GroupInviteGetInviteDetailsSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>>
export type GroupInviteGetInviteDetailsSuspenseQueryError = ErrorType<void>


export function useGroupInviteGetInviteDetailsSuspense<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteGetInviteDetailsSuspense<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGroupInviteGetInviteDetailsSuspense<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get invitation details by code (public endpoint)
 */

export function useGroupInviteGetInviteDetailsSuspense<TData = Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError = ErrorType<void>>(
 code: string, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof groupInviteGetInviteDetails>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGroupInviteGetInviteDetailsSuspenseQueryOptions(code,options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary Delete an invite for a group owned by the current user
 */
export const groupInviteDeleteInvite = (
    id: number,
    inviteId: number,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/group/${id}/invite/${inviteId}`, method: 'DELETE', signal
    },
      options);
    }




export const getGroupInviteDeleteInviteMutationKey = () => ['groupInviteDeleteInvite'] as const;

export const getGroupInviteDeleteInviteMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupInviteDeleteInvite>>, TError,GroupInviteDeleteInviteMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof groupInviteDeleteInvite>>, TError,GroupInviteDeleteInviteMutationVariables, TContext> => {

const mutationKey = getGroupInviteDeleteInviteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof groupInviteDeleteInvite>>, GroupInviteDeleteInviteMutationVariables> = (props) => {
          const {id,inviteId} = props ?? {};

          return  groupInviteDeleteInvite(id,inviteId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GroupInviteDeleteInviteMutationResult = NonNullable<Awaited<ReturnType<typeof groupInviteDeleteInvite>>>

    export type GroupInviteDeleteInviteMutationError = ErrorType<void>
    export type GroupInviteDeleteInviteMutationVariables = {id: number;inviteId: number}

    /**
 * @summary Delete an invite for a group owned by the current user
 */
export const useGroupInviteDeleteInvite = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof groupInviteDeleteInvite>>, TError,GroupInviteDeleteInviteMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof groupInviteDeleteInvite>>,
        TError,
        GroupInviteDeleteInviteMutationVariables,
        TContext
      > => {
      return useMutation(getGroupInviteDeleteInviteMutationOptions(options), queryClient);
    }
